import { useEffect, useRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";

type GlbPreviewProps = {
  bodyCenter?: number[];
  directions?: Record<string, number[]>;
  emptyLabel?: string | null;
  glbUrl: string | null;
  isPlaying?: boolean;
  playbackTime?: number;
  playbackSpeed?: number;
  onDurationChange?: (duration: number) => void;
  onError: (message: string) => void;
  onModelHeightChange?: (height: number) => void;
  onReady: () => void;
  onTimeUpdate?: (time: number) => void;
  sharedModelHeight?: number;
  viewCommand?: GlbViewCommand;
  visualizationNodeName?: string | null;
};

export type GlbViewCommand = {
  angle: number;
  animated: boolean;
  orbitDistance: number;
  revision: number;
};

type CameraViewContext = {
  animationFrame: number | null;
  baseDistance: number;
  camera: THREE.PerspectiveCamera;
  center: THREE.Vector3;
  controls: OrbitControls;
  directions?: Record<string, number[]>;
  modelBounds: {
    center: THREE.Vector3;
    height: number;
    minY: number;
  };
  modelContainer: THREE.Group;
};

export const defaultOrbitDistance = 3.6;
export const minimumOrbitDistance = 2.1;
export const maximumOrbitDistance = 8.1;
export const orbitDistanceStep = 0.5;

const comparisonModelTargetHeight = 2.35;
const comparisonModelVerticalCenter = 0.18;

const visualizationNodeNames = new Set([
  "LeftElbowVisualization",
  "RightElbowVisualization",
  "LeftShoulderHipVisualization",
  "RightShoulderHipVisualization",
  "LeftShoulderArmVisualization",
  "RightShoulderArmVisualization",
  "LeftShoulderVisualization",
  "RightShoulderVisualization",
  "LeftHipAngleVisualization",
  "RightHipAngleVisualization",
  "LeftHipRotationVisualization",
  "RightHipRotationVisualization",
  "LeftKneeAngleVisualization",
  "RightKneeAngleVisualization",
  "TrunkFlexionVisualization",
  "TrunkLateralTiltVisualization",
  "TrunkRotationVisualization",
  "CenterOfGravityVisualization",
]);

export function GlbPreview({
  bodyCenter,
  directions,
  emptyLabel = "等待模型文件",
  glbUrl,
  isPlaying = false,
  playbackSpeed = 1,
  playbackTime,
  onDurationChange,
  onError,
  onModelHeightChange,
  onReady,
  onTimeUpdate,
  sharedModelHeight,
  viewCommand,
  visualizationNodeName = null,
}: GlbPreviewProps) {
  const mountRef = useRef<HTMLDivElement | null>(null);
  const mixerRef = useRef<THREE.AnimationMixer | null>(null);
  const modelRef = useRef<THREE.Object3D | null>(null);
  const durationRef = useRef(0);
  const isPlayingRef = useRef(isPlaying);
  const playbackSpeedRef = useRef(playbackSpeed);
  const onDurationChangeRef = useRef(onDurationChange);
  const onErrorRef = useRef(onError);
  const onModelHeightChangeRef = useRef(onModelHeightChange);
  const onReadyRef = useRef(onReady);
  const onTimeUpdateRef = useRef(onTimeUpdate);
  const sharedModelHeightRef = useRef(sharedModelHeight);
  const visualizationNodeNameRef = useRef(visualizationNodeName);
  const cameraViewRef = useRef<CameraViewContext | null>(null);
  const viewCommandRef = useRef(viewCommand);

  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  useEffect(() => {
    playbackSpeedRef.current = playbackSpeed;
  }, [playbackSpeed]);

  useEffect(() => {
    onTimeUpdateRef.current = onTimeUpdate;
  }, [onTimeUpdate]);

  useEffect(() => {
    onDurationChangeRef.current = onDurationChange;
  }, [onDurationChange]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    onModelHeightChangeRef.current = onModelHeightChange;
  }, [onModelHeightChange]);

  useEffect(() => {
    onReadyRef.current = onReady;
  }, [onReady]);

  useEffect(() => {
    visualizationNodeNameRef.current = visualizationNodeName;
    applyMetricVisualization(modelRef.current, visualizationNodeName);
  }, [visualizationNodeName]);

  useEffect(() => {
    viewCommandRef.current = viewCommand;
    if (viewCommand && cameraViewRef.current) {
      applyControlledView(cameraViewRef.current, viewCommand);
    }
  }, [viewCommand]);

  useEffect(() => {
    sharedModelHeightRef.current = sharedModelHeight;
    const cameraView = cameraViewRef.current;
    if (!cameraView) return;

    applySharedModelScale(cameraView, sharedModelHeight);
    const command = viewCommandRef.current;
    if (command) {
      applyControlledView(cameraView, { ...command, animated: false });
    }
  }, [sharedModelHeight]);

  useEffect(() => {
    if (typeof playbackTime !== "number" || !Number.isFinite(playbackTime)) return;
    const mixer = mixerRef.current;
    if (!mixer) return;
    const duration = durationRef.current;
    mixer.setTime(duration > 0 ? clamp(playbackTime, 0, duration) : Math.max(0, playbackTime));
  }, [playbackTime]);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount || !glbUrl) return;

    if (!hasWebGL()) {
      onErrorRef.current("当前 WebView 不支持 WebGL，无法渲染 GLB");
      return;
    }

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: "high-performance"
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x101512);

    const camera = new THREE.PerspectiveCamera(42, mount.clientWidth / mount.clientHeight, 0.01, 100);
    camera.position.set(0, 1.4, 3.2);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.enablePan = true;
    controls.minDistance = 0.6;
    controls.maxDistance = 8;

    scene.add(new THREE.HemisphereLight(0xffffff, 0x24382c, 2.1));
    const keyLight = new THREE.DirectionalLight(0xffffff, 2);
    keyLight.position.set(3, 5, 4);
    scene.add(keyLight);

    let mixer: THREE.AnimationMixer | null = null;
    let disposed = false;
    const clock = new THREE.Clock();
    mixerRef.current = null;
    modelRef.current = null;
    durationRef.current = 0;

    const loader = new GLTFLoader();
    loader.load(
      glbUrl,
      (gltf) => {
        if (disposed) return;

        const model = gltf.scene;
        modelRef.current = model;
        applyMetricVisualization(model, visualizationNodeNameRef.current);
        const modelContainer = new THREE.Group();
        modelContainer.add(model);
        scene.add(modelContainer);
        const cameraView = prepareCameraView(
          camera,
          controls,
          modelContainer,
          model,
          bodyCenter,
          directions,
          sharedModelHeightRef.current,
        );
        cameraViewRef.current = cameraView;
        onModelHeightChangeRef.current?.(cameraView.modelBounds.height);
        const command = viewCommandRef.current;
        if (command) {
          applyControlledView(cameraView, { ...command, animated: false });
        } else {
          applyControlledView(cameraView, {
            angle: 0,
            animated: false,
            orbitDistance: defaultOrbitDistance,
            revision: 0,
          });
        }

        if (gltf.animations.length > 0) {
          mixer = new THREE.AnimationMixer(model);
          mixerRef.current = mixer;
          durationRef.current = Math.max(...gltf.animations.map((clip) => clip.duration), 0);
          onDurationChangeRef.current?.(durationRef.current);
          gltf.animations.forEach((clip) => {
            const action = mixer?.clipAction(clip);
            action?.play();
          });
        } else {
          onDurationChangeRef.current?.(0);
        }

        onReadyRef.current();
      },
      undefined,
      (error) => {
        console.error(error);
        onErrorRef.current("GLB 加载失败，可能是文件损坏或 WebView 不支持当前模型");
      }
    );

    function animate() {
      if (disposed) return;
      requestAnimationFrame(animate);
      const delta = clock.getDelta();
      if (mixer && isPlayingRef.current) {
        mixer.update(delta * playbackSpeedRef.current);
        const duration = durationRef.current;
        if (duration > 0) {
          const time = mixer.time % duration;
          onTimeUpdateRef.current?.(time);
        }
      }
      controls.update();
      renderer.render(scene, camera);
    }

    function resize() {
      if (!mount) return;
      const width = mount.clientWidth;
      const height = mount.clientHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    }

    window.addEventListener("resize", resize);
    animate();

    return () => {
      disposed = true;
      window.removeEventListener("resize", resize);
      controls.dispose();
      renderer.dispose();
      if (typeof cameraViewRef.current?.animationFrame === "number") {
        window.cancelAnimationFrame(cameraViewRef.current.animationFrame);
      }
      cameraViewRef.current = null;
      mixerRef.current = null;
      modelRef.current = null;
      durationRef.current = 0;
      scene.traverse((object) => {
        const mesh = object as THREE.Mesh;
        mesh.geometry?.dispose?.();
        const material = mesh.material;
        if (Array.isArray(material)) {
          material.forEach((item) => item.dispose());
        } else {
          material?.dispose?.();
        }
      });
      renderer.domElement.remove();
    };
  }, [bodyCenter, directions, glbUrl]);

  return (
    <div className="glb-preview" ref={mountRef}>
      {!glbUrl && emptyLabel ? <div className="empty-state">{emptyLabel}</div> : null}
    </div>
  );
}

function applyMetricVisualization(model: THREE.Object3D | null, selectedNodeName: string | null) {
  if (!model) return;

  model.traverse((object) => {
    if (!visualizationNodeNames.has(object.name)) return;
    object.visible = object.name === selectedNodeName;
    if (object.visible && shouldTintVisualization(object.name)) {
      tintVisualizationSubtree(object);
    }
  });
}

function shouldTintVisualization(nodeName: string) {
  return !nodeName.endsWith("ShoulderVisualization") && !nodeName.endsWith("HipRotationVisualization");
}

function tintVisualizationSubtree(root: THREE.Object3D) {
  root.traverse((object) => {
    const mesh = object as THREE.Mesh;
    if (!mesh.isMesh || mesh.userData.pingPiMetricTinted) return;

    const tintMaterial = (source: THREE.Material) => {
      const material = source.clone() as THREE.Material & { color?: THREE.Color };
      material.color?.set(0x006ff5);
      return material;
    };

    mesh.material = Array.isArray(mesh.material)
      ? mesh.material.map(tintMaterial)
      : tintMaterial(mesh.material);
    mesh.userData.pingPiMetricTinted = true;
  });
}

function hasWebGL() {
  const canvas = document.createElement("canvas");
  return Boolean(canvas.getContext("webgl2") || canvas.getContext("webgl"));
}

function prepareCameraView(
  camera: THREE.PerspectiveCamera,
  controls: OrbitControls,
  modelContainer: THREE.Group,
  object: THREE.Object3D,
  bodyCenter?: number[],
  directions?: Record<string, number[]>,
  sharedModelHeight?: number,
): CameraViewContext {
  const displayObject = object.getObjectByName("BodyNode") ?? object;
  const box = new THREE.Box3().setFromObject(displayObject);
  const size = box.getSize(new THREE.Vector3());
  const measuredCenter = box.getCenter(new THREE.Vector3());
  const fallbackCenter = vectorFromArray(bodyCenter) ?? new THREE.Vector3();
  const measuredHeight = size.y;
  const height = Number.isFinite(measuredHeight) && measuredHeight > 0.000001
    ? measuredHeight
    : 1;
  const boundsCenter = vectorIsFinite(measuredCenter) ? measuredCenter : fallbackCenter;
  const minY = Number.isFinite(box.min.y) ? box.min.y : boundsCenter.y - height / 2;
  const center = new THREE.Vector3(0, comparisonModelVerticalCenter, 0);
  controls.target.copy(center);
  controls.minDistance = minimumOrbitDistance;
  controls.maxDistance = maximumOrbitDistance;

  const context: CameraViewContext = {
    animationFrame: null,
    baseDistance: defaultOrbitDistance,
    camera,
    center,
    controls,
    directions,
    modelBounds: {
      center: boundsCenter,
      height,
      minY,
    },
    modelContainer,
  };
  applySharedModelScale(context, sharedModelHeight);
  return context;
}

function applySharedModelScale(context: CameraViewContext, sharedModelHeight?: number) {
  const referenceHeight = typeof sharedModelHeight === "number"
    && Number.isFinite(sharedModelHeight)
    && sharedModelHeight > 0.000001
    ? sharedModelHeight
    : context.modelBounds.height;
  const scale = comparisonModelTargetHeight / referenceHeight;
  const bounds = context.modelBounds;

  context.modelContainer.scale.setScalar(scale);
  context.modelContainer.position.set(
    -bounds.center.x * scale,
    comparisonModelVerticalCenter - comparisonModelTargetHeight / 2 - bounds.minY * scale,
    -bounds.center.z * scale,
  );
}

function applyControlledView(context: CameraViewContext, command: GlbViewCommand) {
  if (context.animationFrame !== null) {
    window.cancelAnimationFrame(context.animationFrame);
    context.animationFrame = null;
  }

  const dampingWasEnabled = context.controls.enableDamping;
  context.controls.enableDamping = false;
  context.controls.update();
  context.controls.enableDamping = dampingWasEnabled;

  const distance = context.baseDistance * clamp(
    command.orbitDistance,
    minimumOrbitDistance,
    maximumOrbitDistance,
  ) / defaultOrbitDistance;
  const destination = cameraPositionForAngle(
    command.angle,
    context.center,
    distance,
    context.directions,
  );

  const updateCamera = (position: THREE.Vector3) => {
    context.camera.position.copy(position);
    context.camera.near = Math.max(distance / 100, 0.01);
    context.camera.far = distance * 100;
    context.camera.updateProjectionMatrix();
    context.controls.target.copy(context.center);
    context.controls.update();
  };

  if (!command.animated || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    updateCamera(destination);
    return;
  }

  const origin = context.camera.position.clone();
  const startedAt = performance.now();
  const duration = 240;

  const animate = (timestamp: number) => {
    const progress = clamp((timestamp - startedAt) / duration, 0, 1);
    const eased = 1 - Math.pow(1 - progress, 3);
    updateCamera(origin.clone().lerp(destination, eased));
    if (progress < 1) {
      context.animationFrame = window.requestAnimationFrame(animate);
    } else {
      context.animationFrame = null;
    }
  };

  context.animationFrame = window.requestAnimationFrame(animate);
}

function cameraPositionForAngle(
  angle: number,
  center: THREE.Vector3,
  distance: number,
  directions?: Record<string, number[]>,
) {
  const vectors = horizontalDirectionVectors(directions);
  if (!vectors) {
    const radians = normalizeAngle(angle) * Math.PI / 180;
    return new THREE.Vector3(
      center.x + Math.sin(radians) * distance,
      center.y,
      center.z + Math.cos(radians) * distance,
    );
  }

  const normalized = normalizeAngle(angle);
  const lowerIndex = Math.floor(normalized / 45) % vectors.length;
  const upperIndex = (lowerIndex + 1) % vectors.length;
  const progress = (normalized - lowerIndex * 45) / 45;
  const lower = vectors[lowerIndex];
  const upper = vectors[upperIndex];
  const directionX = lower.x + (upper.x - lower.x) * progress;
  const directionZ = lower.z + (upper.z - lower.z) * progress;

  return new THREE.Vector3(
    center.x - directionX * distance,
    center.y,
    center.z - directionZ * distance,
  );
}

function horizontalDirectionVectors(directions?: Record<string, number[]>) {
  if (!directions) return null;
  const keys = [
    "front",
    "front_right",
    "right",
    "back_right",
    "back",
    "back_left",
    "left",
    "front_left",
  ];
  const vectors = keys.map((key) => {
    const value = vectorFromArray(directions[key]);
    if (!value || Math.hypot(value.x, value.z) <= 0.000001) return null;
    return { x: value.x, z: value.z };
  });
  return vectors.every((value) => value !== null)
    ? vectors as Array<{ x: number; z: number }>
    : null;
}

function normalizeAngle(angle: number) {
  const remainder = angle % 360;
  return remainder >= 0 ? remainder : remainder + 360;
}

function vectorFromArray(value?: number[]) {
  if (!value || value.length < 3) return null;
  const [x, y, z] = value;
  if (![x, y, z].every((item) => typeof item === "number" && Number.isFinite(item))) return null;
  return new THREE.Vector3(x, y, z);
}

function vectorIsFinite(value: THREE.Vector3) {
  return [value.x, value.y, value.z].every(Number.isFinite);
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}
